/**
 * Das Bild, das den START-Knopf ersetzt — EINE Auflösung für alle Oberflächen.
 *
 * DAS PROBLEM (Jans Fund 05.08.2026: „der START-Button funktioniert nicht im
 * Template Editor, wenn man ein Bild hochlädt"): Das Bild wird im
 * Startbildschirm-Menü des Pro-Editors hochgeladen und landet dort an der
 * START-Slide (`slide.startButtonImage`). Vorschau und Leser zeichnen das Cover
 * aber aus den METADATEN (`metadata.startButtonImage`) — ein Feld, das bis
 * dahin niemand füllte. Ergebnis: Im Pro-Editor lag das Bild, überall sonst
 * stand weiter der Standard-Knopf.
 *
 * Der Weg dorthin ist die Spiegelung beim Speichern (app/src/lib/cover-meta).
 * Diese Auflösung hier ist das Gegenstück fürs Lesen — mit dem RÜCKFALL auf die
 * Slide, damit auch Scrollys richtig aussehen, die vor der Spiegelung
 * gespeichert wurden. Ohne den müsste jeder Bestands-Scrolly erst einmal im
 * Pro-Editor neu gespeichert werden, damit ein längst hochgeladenes Bild
 * erscheint.
 *
 * VORRANG: Metadaten vor Slide. Die Metadaten sind der gespiegelte, aktuelle
 * Stand; die Slide ist die Quelle, aus der gespiegelt wird.
 */

/** Bild-Angabe, wie sie am Slide bzw. in den Metadaten steht. */
export interface StartButtonImageLike {
    url?: string;
    /** Vom Nutzer hochgeladene Fassung — schlägt das Original. */
    ppiUrl?: string;
    width?: number;
    height?: number;
}

interface StartSlideLike {
    from?: number;
    startButtonImage?: unknown;
}

function text(wert: unknown): string {
    return typeof wert === "string" ? wert.trim() : "";
}

/** Adresse einer Bild-Angabe; leer, wenn keine brauchbare drinsteht. */
export function startButtonImageUrl(bild: unknown): string {
    if (!bild || typeof bild !== "object") return "";
    const b = bild as StartButtonImageLike;
    return text(b.ppiUrl) || text(b.url);
}

/**
 * Welche Adresse zeichnet der Knopf? Leer = normaler Knopf mit Beschriftung.
 *
 * DIE START-SLIDE ist die bei `from === 0` — dieselbe Regel, nach der auch der
 * Renderer den Startbildschirm erkennt. NICHT „die erste im Array": ein
 * umsortiertes Dokument hätte sonst die falsche erwischt.
 */
export function resolveStartButtonImage(
    metadata: { startButtonImage?: unknown } | null | undefined,
    slides: ReadonlyArray<StartSlideLike | null | undefined> | null | undefined,
): string {
    const ausMetadaten = startButtonImageUrl(metadata?.startButtonImage);
    if (ausMetadaten) return ausMetadaten;
    if (!Array.isArray(slides)) return "";
    const start = slides.find((s) => s && s.from === 0);
    return startButtonImageUrl(start?.startButtonImage);
}

// ── START-Knopf-GESTALTUNG (Jans Fund 24.08.2026) ───────────────────────────
//
// Der Startbildschirm des Pro-Editors kann den Knopf über das Bild hinaus
// GESTALTEN: Eckenradius, Füllfarbe, Randfarbe, Randbreite, Akzentfarbe
// (Text/Kontur). Diese Felder liegen an der START-Slide (start*-Felder) und
// wurden — anders als Text und Bild — nie in die Metadaten gespiegelt:
// Vorschau und Leser zeigten deshalb den Standard-Knopf. Hier die geteilte
// Auflösung (Metadaten-Vorrang, Slide-Rückfall — dasselbe Muster wie beim
// Knopf-Bild), damit alle Oberflächen dieselbe Gestalt zeichnen.

/** Farbwert, wie der Pro-Editor ihn speichert. */
export interface RgbaLike {
    r?: number;
    g?: number;
    b?: number;
    a?: number;
}

/** CSS-Farbe aus einem Pro-Editor-Farbobjekt; leer, wenn keins. */
export function rgbaCss(farbe: unknown): string {
    if (!farbe || typeof farbe !== "object") return "";
    const f = farbe as RgbaLike;
    if (typeof f.r !== "number") return "";
    return `rgba(${f.r}, ${f.g ?? 0}, ${f.b ?? 0}, ${f.a ?? 1})`;
}

/**
 * Die Akzentfarbe nur, wenn sie bewusst gewaehlt wurde: der Pro-Editor
 * spiegelt seine Vorgabe (reines, deckendes Weiss) immer mit — die zaehlt
 * nicht als Wahl. Erwartet den rgba()-String aus `resolveStartButtonStyle`.
 */
export function bewussteAkzentfarbe(farbe: string | undefined): string | undefined {
    if (!farbe) return undefined;
    const m = farbe.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
    if (m && m[1] === '255' && m[2] === '255' && m[3] === '255' && (m[4] === undefined || Number(m[4]) === 1)) {
        return undefined;
    }
    return farbe;
}

/** Gestaltung des START-Knopfs, fertig als CSS-Werte aufgelöst. */
export interface StartButtonStyle {
    /** Eckenradius in px (Pro-Editor-Vorgabe: 100). */
    radius?: number;
    /** Füllfarbe; leer = transparent (Pro-Editor-Vorgabe). */
    fillColor?: string;
    /** Randfarbe; leer = Akzentfarbe. */
    borderColor?: string;
    /** Randbreite in px (Pro-Editor-Vorgabe: 2, bis 03.10.2026: 3). */
    borderWidth?: number;
    /** Akzentfarbe (Text + Kontur-Rückfall). */
    accentColor?: string;
    /**
     * STANDARD-KNOPF statt Gestaltung (Schalter „Standard-Button (Pill-Farbe)“).
     * Dann steht hier höchstens noch die Akzentfarbe — sie färbt weiter
     * Dachzeile, Titel und Unterzeile, nur der Knopf bleibt der Standard.
     */
    standard?: boolean;
}

interface StartButtonStyleQuelle {
    startButtonStandard?: unknown;
    startButtonRadius?: unknown;
    startButtonFillColor?: unknown;
    startButtonBorderColor?: unknown;
    startButtonBorderWidth?: unknown;
    startAccentColor?: unknown;
}

/** Ganz durchsichtig (Deckkraft 0) — sieht aus wie „keine Farbe". */
function istDurchsichtig(farbe: unknown): boolean {
    return Boolean(farbe) && typeof farbe === "object" && (farbe as RgbaLike).a === 0;
}

/** Deckendes Weiß — die Farb-Vorgabe des Pro-Editors. */
function istWeiss(farbe: unknown): boolean {
    if (!farbe || typeof farbe !== "object") return false;
    const f = farbe as RgbaLike;
    return f.r === 255 && f.g === 255 && f.b === 255 && (f.a === undefined || f.a === 1);
}

function stilAus(quelle: StartButtonStyleQuelle | null | undefined): StartButtonStyle | null {
    if (!quelle) return null;
    const stil: StartButtonStyle = {};
    if (typeof quelle.startButtonRadius === "number") stil.radius = quelle.startButtonRadius;
    // Eine ganz durchsichtige Füllung ist keine: sonst liefe der Ladebalken
    // unsichtbar in Durchsichtig statt in der Akzentfarbe (CoverSection).
    const fill = istDurchsichtig(quelle.startButtonFillColor) ? "" : rgbaCss(quelle.startButtonFillColor);
    if (fill) stil.fillColor = fill;
    const rand = rgbaCss(quelle.startButtonBorderColor);
    if (rand) stil.borderColor = rand;
    // 3 war bis 03.10.2026 die Vorgabe und steht deshalb in fast jedem älteren
    // Dokument, ohne dass jemand sie gewählt hat → als alte Vorgabe 2 zeichnen
    // (Jans Wahl: lieber überall 2 px, bewusst 3 gibt es damit nicht mehr).
    // Der Pro-Editor zieht beim Öffnen dasselbe nach (Slide-Snapshot).
    const breite = quelle.startButtonBorderWidth;
    if (typeof breite === "number") stil.borderWidth = breite === 3 ? 2 : breite;
    const akzent = rgbaCss(quelle.startAccentColor);
    if (akzent) stil.accentColor = akzent;
    return Object.keys(stil).length > 0 ? stil : null;
}

// ── STANDARD-KNOPF (Jans Wunsch 03.10.2026) ─────────────────────────────────
//
// Der Pro-Editor speichert seine Knopf-VORGABEN (Radius 100, Rand 2 — bis
// 03.10.2026: 3 —, Weiß, keine Füllung) in JEDES Dokument, auch wenn niemand
// etwas eingestellt hat. Seit der Spiegelung (24.08.2026) hielt der Leser das
// für eine Gestaltung und zeigte statt des Standard-Knopfs (Pill-Farbe aus dem
// Branding, sonst Magenta) einen weißen Umriss (auf prod 87 von 107
// Templates). Deshalb jetzt:
//   - die reinen Vorgaben sind KEINE Gestaltung (→ Standard-Knopf);
//   - der Schalter `startButtonStandard` im Pro-Editor sagt es ausdrücklich:
//     true = immer der Standard-Knopf, false = immer die Gestaltung (auch wer
//     den weißen Umriss bewusst will), ungesetzt = aus den Werten abgeleitet.

/** Stehen nur die Vorgaben des Pro-Editors da (oder gar nichts)? */
function nurGrundwerte(q: StartButtonStyleQuelle): boolean {
    const radius = q.startButtonRadius;
    const rand = q.startButtonBorderWidth;
    return (
        (typeof radius !== "number" || radius === 100) &&
        // 2 = Vorgabe seit 03.10.2026, 3 = die alte, die ältere Dokumente tragen
        (typeof rand !== "number" || rand === 2 || rand === 3) &&
        (!rgbaCss(q.startAccentColor) || istWeiss(q.startAccentColor)) &&
        (!rgbaCss(q.startButtonFillColor) || istDurchsichtig(q.startButtonFillColor)) &&
        (!rgbaCss(q.startButtonBorderColor) || istWeiss(q.startButtonBorderColor))
    );
}

/**
 * Zeigt dieser Startbildschirm den STANDARD-Knopf? Der ausdrückliche
 * Schalter gewinnt, sonst entscheiden die Werte. Erwartet die Start-Slide
 * oder die Metadaten — Pro-Editor (Menü-Haken, Bühne) und Leser fragen hier
 * dasselbe.
 */
export function startKnopfIstStandard(quelle: object | null | undefined): boolean {
    const q = quelle as StartButtonStyleQuelle | null | undefined;
    if (!q) return true;
    if (typeof q.startButtonStandard === "boolean") return q.startButtonStandard;
    return nurGrundwerte(q);
}

/**
 * Gestaltung auflösen: Metadaten (gespiegelter Stand) vor Start-Slide
 * (Quelle; Rückfall für Scrollys von vor der Spiegelung). `null` = nichts
 * gestaltet → der Standard-Knopf bleibt exakt wie bisher.
 */
export function resolveStartButtonStyle(
    // `object` statt der Quell-Struktur: alle Quell-Felder sind optional —
    // TS' Weak-Type-Prüfung lehnte sonst Metadaten-Typen ab, die keins der
    // Felder deklarieren (sie tragen sie zur Laufzeit trotzdem).
    metadata: object | null | undefined,
    slides: ReadonlyArray<object | null | undefined> | null | undefined,
): StartButtonStyle | null {
    const m = metadata as StartButtonStyleQuelle | null | undefined;
    let quelle: StartButtonStyleQuelle | null = null;
    if (m && (stilAus(m) || typeof m.startButtonStandard === "boolean")) {
        quelle = m;
    } else if (Array.isArray(slides)) {
        quelle = (slides.find((s) => s && (s as StartSlideLike).from === 0) ??
            null) as StartButtonStyleQuelle | null;
    }
    const stil = stilAus(quelle);
    if (startKnopfIstStandard(quelle)) {
        const akzent = bewussteAkzentfarbe(stil?.accentColor);
        return akzent ? { standard: true, accentColor: akzent } : null;
    }
    // Ausdrücklich gestaltet, aber ohne jeden Wert: die Vorgaben des
    // Pro-Editors zeichnen (Radius 100, Rand 2, Weiß — Rückfälle in CoverSection).
    return stil ?? { radius: 100 };
}
